/**
 * Profile Export/Import Utility
 * 
 * Allows Premium users to export and import their profile data.
 * Export is profile-independent and can be imported by another Premium user.
 * 
 * Includes:
 * - Inventory items (Förråd)
 * - Water settings and containers (Vatten)
 * - Shopping lists, items and favorites (Inköpslista)
 * - Checkbox status on standard lists + custom lists (Checklista)
 * - Year wheel tasks (Årshjul)
 * - Notes (Anteckningar)
 * - Read status (Kunskap)
 * 
 * Excludes:
 * - Family settings
 * - User settings
 */

import { getApiUrl } from './api';
import { useAuthStore } from '@/stores/auth-store';

export interface ProfileExport {
  version: string; // Schema version for future compatibility
  exportedAt: string; // ISO timestamp
  data: {
    inventory?: {
      items: any[]; // Inventory items from /api/inventory
    };
    water?: {
      // Flattened water settings to match /api/water/import
      householdSize?: number;
      pets?: number;
      targetDays?: number;
      containers: any[]; // Water containers
    };
    shopping?: {
      lists: any[]; // Shopping lists
      items: any[]; // Shopping list items
      favorites: any[]; // Favorite items
    };
    checklists?: {
      // Standard checklist progress (by area key)
      checklists: Record<string, any[]>; // Matches /api/checklists & /api/checklists/import
      // Legacy/unused fields kept for backwards-compat
      standardProgress?: Record<string, any>;
      customLists?: any[];
    };
    customChecklists?: {
      lists: any[]; // Custom checklist lists
      items: any[]; // Custom checklist items
    };
    yearWheel?: {
      tasks: any[]; // Year wheel tasks
    };
    notes?: {
      items: any[]; // Notes
    };
    knowledge?: {
      readArticles: any[]; // Read articles from user.readArticles
    };
    recipes?: {
      recipes: any[]; // User/family recipes
      mealPlan: any[]; // Meal plan items
    };
  };
}

/**
 * Export user profile data to JSON
 */
export async function exportProfile(): Promise<ProfileExport> {
  const token = useAuthStore.getState().accessToken;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = {
    'Authorization': `Bearer ${token}`,
  };

  // Align export with current family context so we export the same
  // data the user sees in the UI (household vs personal scope).
  let familyQuery = '';
  try {
    const { getEffectiveFamilyOwnerId } = await import('@/stores/family-store');
    const authState = useAuthStore.getState();
    const ownerId = getEffectiveFamilyOwnerId();
    const familyId = ownerId || authState.user?.familyId;
    if (familyId) {
      familyQuery = `?familyId=${encodeURIComponent(familyId)}`;
    }
  } catch {
    // If family-store cannot be loaded for some reason, fall back to
    // exporting personal data only.
    familyQuery = '';
  }

  // Fetch all data in parallel
  const [
    inventoryRes,
    waterRes,
    shoppingRes,
    checklistsRes,
    yearWheelRes,
    notesRes,
    favoritesRes,
    customChecklistsRes,
    recipesRes,
    mealPlanRes,
  ] = await Promise.all([
    fetch(getApiUrl(`inventory${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`water${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`shopping-lists/get${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`checklists${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`year-wheel${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`notes${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`favorites${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`custom-checklists/get${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`recipes${familyQuery}`), { headers }).catch(() => null),
    fetch(getApiUrl(`meal-plan${familyQuery}`), { headers }).catch(() => null),
  ]);

  const profileData: ProfileExport = {
    version: '1.1.0',
    exportedAt: new Date().toISOString(),
    data: {},
  };

  // Inventory
  if (inventoryRes?.ok) {
    const invJson = await inventoryRes.json();
    const invData = invJson?.data ?? invJson;
    profileData.data.inventory = {
      items: invData.items || invData || [],
    };
  }

  // Water
  if (waterRes?.ok) {
    const waterJson = await waterRes.json();
    const waterData = waterJson?.data ?? waterJson;
    profileData.data.water = {
      householdSize: waterData.householdSize,
      pets: waterData.pets,
      targetDays: waterData.targetDays,
      containers: waterData.containers || [],
    };
  }

  // Shopping
  if (shoppingRes?.ok) {
    const shoppingJson = await shoppingRes.json();
    const shoppingData = shoppingJson?.data ?? shoppingJson;
    profileData.data.shopping = {
      lists: shoppingData.lists || [],
      items: shoppingData.items || [],
      favorites: [], // Filled from separate favorites endpoint below
    };
  }

  // Checklists
  if (checklistsRes?.ok) {
    const checklistJson = await checklistsRes.json();
    const checklistData = checklistJson?.data ?? checklistJson;
    profileData.data.checklists = {
      checklists: checklistData.checklists || {},
    };
  }

  // Year Wheel
  if (yearWheelRes?.ok) {
    const yearWheelJson = await yearWheelRes.json();
    const yearWheelData = yearWheelJson?.data ?? yearWheelJson;
    profileData.data.yearWheel = {
      tasks: yearWheelData.tasks || yearWheelData || [],
    };
  }

  // Notes
  if (notesRes?.ok) {
    const notesJson = await notesRes.json();
    const notesData = notesJson?.data ?? notesJson;
    profileData.data.notes = {
      items: notesData.notes || notesData || [],
    };
  }

   // Favorites (shopping)
  if (favoritesRes?.ok) {
    const favoritesJson = await favoritesRes.json();
    const favoritesData = favoritesJson?.data ?? favoritesJson;
    if (!profileData.data.shopping) {
      profileData.data.shopping = {
        lists: [],
        items: [],
        favorites: favoritesData.favorites || [],
      };
    } else {
      profileData.data.shopping.favorites = favoritesData.favorites || [];
    }
  }

  // Custom checklists (egna listor)
  if (customChecklistsRes?.ok) {
    const customJson = await customChecklistsRes.json();
    const customData = customJson?.data ?? customJson;
    profileData.data.customChecklists = {
      lists: customData.lists || [],
      items: customData.items || [],
    };
  }

  // Recipes & Meal plan (Receptbok)
  if (recipesRes?.ok || mealPlanRes?.ok) {
    const recipesJson = recipesRes?.ok ? await recipesRes.json() : { recipes: [] };
    const recipesData = (recipesJson as any)?.data ?? recipesJson;
    const mealPlanJson = mealPlanRes?.ok ? await mealPlanRes.json() : [];
    const mealPlanData = (mealPlanJson as any)?.data ?? mealPlanJson;
    profileData.data.recipes = {
      recipes: recipesData.recipes || [],
      mealPlan: Array.isArray(mealPlanData) ? mealPlanData : [],
    };
  }

  // Knowledge read status (from auth store user)
  const authState = useAuthStore.getState();
  const readArticles = authState.user?.readArticles || [];
  if (readArticles.length > 0) {
    profileData.data.knowledge = {
      readArticles,
    };
  }

  return profileData;
}

/**
 * Import profile data from JSON
 */
export async function importProfile(profileData: ProfileExport): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  // Import should target the same scope the user is currently
  // working in: personal profile or household (family). We do this
  // by reusing the effective family id, but we never persist that
  // id inside the import payload itself.
  let familyQuery = '';
  try {
    const { getEffectiveFamilyOwnerId } = await import('@/stores/family-store');
    const authState = useAuthStore.getState();
    const ownerId = getEffectiveFamilyOwnerId();
    const familyId = ownerId || authState.user?.familyId;
    if (familyId) {
      familyQuery = `?familyId=${encodeURIComponent(familyId)}`;
    }
  } catch {
    familyQuery = '';
  }

  const results = {
    inventory: false,
    water: false,
    shopping: false,
    checklists: false,
    yearWheel: false,
    notes: false,
    favorites: false,
    customChecklists: false,
    recipes: false,
    mealPlan: false,
    knowledge: false,
  };

  // Import inventory
  if (profileData.data.inventory?.items) {
    try {
      const res = await fetch(getApiUrl(`inventory/import${familyQuery}`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ items: profileData.data.inventory.items }),
      });
      results.inventory = res.ok;
    } catch (err) {
      console.error('Failed to import inventory:', err);
    }
  }

  // Import water
  if (profileData.data.water) {
    try {
      const water = profileData.data.water;
      // Support both legacy { settings, containers } and flattened shape
      const body = {
        householdSize: (water as any).householdSize ?? (water as any).settings?.householdSize,
        pets: (water as any).pets ?? (water as any).settings?.pets,
        targetDays: (water as any).targetDays ?? (water as any).settings?.targetDays,
        containers: water.containers || [],
      };
      const res = await fetch(getApiUrl(`water/import${familyQuery}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      results.water = res.ok;
    } catch (err) {
      console.error('Failed to import water:', err);
    }
  }

  // Import shopping
  if (profileData.data.shopping) {
    try {
      const shopping = profileData.data.shopping as any;
      const body: any = {};
      if (Array.isArray(shopping.lists) && shopping.lists.length > 0) {
        body.lists = shopping.lists;
      }
      if (Array.isArray(shopping.items) && shopping.items.length > 0) {
        body.items = shopping.items;
      }

      if (Object.keys(body).length > 0) {
      const res = await fetch(getApiUrl(`shopping-lists/import${familyQuery}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      results.shopping = res.ok;
      } else {
        results.shopping = true; // Nothing to import
      }
    } catch (err) {
      console.error('Failed to import shopping:', err);
    }
  }

  // Import checklists
  if (profileData.data.checklists) {
    try {
      const checklists = profileData.data.checklists as any;
      const body = {
        // New format: { checklists: Record<string, items[]> }
        checklists: checklists.checklists || checklists.standardProgress || {},
      };
      const res = await fetch(getApiUrl(`checklists/import${familyQuery}`), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      results.checklists = res.ok;
    } catch (err) {
      console.error('Failed to import checklists:', err);
    }
  }

  // Import year wheel
  if (profileData.data.yearWheel?.tasks) {
    try {
      const res = await fetch(getApiUrl(`year-wheel/import${familyQuery}`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ tasks: profileData.data.yearWheel.tasks }),
      });
      results.yearWheel = res.ok;
    } catch (err) {
      console.error('Failed to import year wheel:', err);
    }
  }

  // Import notes
  if (profileData.data.notes?.items) {
    try {
      const res = await fetch(getApiUrl(`notes/import${familyQuery}`), {
        method: 'POST',
        headers,
        body: JSON.stringify({ notes: profileData.data.notes.items }),
      });
      results.notes = res.ok;
    } catch (err) {
      console.error('Failed to import notes:', err);
    }
  }

  // Import favorites (shopping)
  if (profileData.data.shopping?.favorites && profileData.data.shopping.favorites.length > 0) {
    try {
      let hadError = false;

      // Clear existing favorites in the current scope so that
      // imported favorites replace, rather than merge with, old ones.
      try {
        const existingRes = await fetch(getApiUrl(`favorites${familyQuery}`), {
          headers: {
            'Authorization': headers['Authorization'],
          },
        });
        if (existingRes.ok) {
          const existingJson = await existingRes.json();
          const existingData = (existingJson as any)?.data ?? existingJson;
          const existingFavorites: any[] = existingData?.favorites || [];
          for (const fav of existingFavorites) {
            const delRes = await fetch(getApiUrl(`favorites/${fav.id}${familyQuery}`), {
              method: 'DELETE',
              headers,
            });
            if (!delRes.ok) {
              hadError = true;
              console.warn('Failed to delete existing favorite before import', {
                id: fav.id,
                status: delRes.status,
                statusText: delRes.statusText,
              });
            }
          }
        } else {
          hadError = true;
          console.warn('Failed to fetch existing favorites before import', {
            status: existingRes.status,
            statusText: existingRes.statusText,
          });
        }
      } catch (clearErr) {
        hadError = true;
        console.error('Failed to clear existing favorites before import:', clearErr);
      }

      // Create favorites from export
      for (const fav of profileData.data.shopping.favorites as any[]) {
        const body = {
          name: fav.name,
          category: fav.category,
          notes: fav.notes,
          link: fav.link,
          store: fav.store,
          aisle: fav.aisle,
        };
        const res = await fetch(getApiUrl(`favorites${familyQuery}`), {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          hadError = true;
          console.warn('Failed to import favorite', {
            name: fav.name,
            status: res.status,
            statusText: res.statusText,
          });
        }
      }

      results.favorites = !hadError;
    } catch (err) {
      console.error('Failed to import favorites:', err);
    }
  } else {
    results.favorites = true;
  }

  // Import custom checklists (egna listor)
  if (profileData.data.customChecklists) {
    try {
      const custom = profileData.data.customChecklists as any;
      const listIdMap = new Map<string, string>();
      let hadError = false;

      // Clear existing custom checklists in the current scope so that
      // imported data replaces, rather than merges with, old lists.
      try {
        const existingRes = await fetch(getApiUrl(`custom-checklists/get${familyQuery}`), {
          headers: {
            'Authorization': headers['Authorization'],
          },
        });
        if (existingRes.ok) {
          const existingJson = await existingRes.json();
          const existingData = (existingJson as any)?.data ?? existingJson;
          const existingLists: any[] = existingData?.lists || [];
          for (const existing of existingLists) {
            const delRes = await fetch(getApiUrl(`custom-checklists/delete/${existing.id}${familyQuery}`), {
              method: 'DELETE',
              headers,
            });
            if (!delRes.ok) {
              hadError = true;
              console.warn('Failed to delete existing custom checklist before import', {
                listId: existing.id,
                status: delRes.status,
                statusText: delRes.statusText,
              });
            }
          }
        } else {
          hadError = true;
          console.warn('Failed to fetch existing custom checklists before import', {
            status: existingRes.status,
            statusText: existingRes.statusText,
          });
        }
      } catch (clearErr) {
        hadError = true;
        console.error('Failed to clear existing custom checklists before import:', clearErr);
      }

      // First create lists
      if (Array.isArray(custom.lists)) {
        for (const list of custom.lists) {
          const res = await fetch(getApiUrl(`custom-checklists/create${familyQuery}`), {
            method: 'POST',
            headers,
            body: JSON.stringify({
              name: list.name,
              showOnDashboard: list.showOnDashboard,
              recurring: list.recurring,
              recurringType: list.recurringType,
              recurringTime: list.recurringTime,
              recurringDay: list.recurringDay,
              color: list.color,
            }),
          });
          if (res.ok) {
            const json = await res.json();
            const env = json as any;
            const created = env?.data?.list ?? env.list ?? env;
            if (created?.id && list.id) {
              listIdMap.set(list.id, created.id);
            }
          } else {
            hadError = true;
            console.warn('Failed to import custom checklist list', {
              status: res.status,
              statusText: res.statusText,
            });
          }
        }
      }

      // Then create items
      if (Array.isArray(custom.items)) {
        for (const item of custom.items) {
          const targetListId = listIdMap.get(item.listId);
          if (!targetListId) continue;
          // Normalisera text så att den alltid uppfyller API-schemat
          const rawText = typeof item.text === 'string' ? item.text : String(item.text ?? '');
          const text = rawText.slice(0, 50) || 'Uppgift';
          const createRes = await fetch(getApiUrl(`custom-checklists/items/create${familyQuery}`), {
            method: 'POST',
            headers,
            body: JSON.stringify({
              listId: targetListId,
              text,
            }),
          });
          if (createRes.ok) {
            const createdJson = await createRes.json();
            const env = createdJson as any;
            const createdItem = env?.data?.item ?? env.item ?? env;
            if (createdItem?.id && item.completed) {
              // Apply completion state
              await fetch(getApiUrl(`custom-checklists/items/update/${createdItem.id}${familyQuery}`), {
                method: 'PUT',
                headers,
                body: JSON.stringify({ completed: true }),
              });
            }
          } else {
            hadError = true;
            console.warn('Failed to import custom checklist item', {
              status: createRes.status,
              statusText: createRes.statusText,
            });
          }
        }
      }

      results.customChecklists = !hadError;
    } catch (err) {
      console.error('Failed to import custom checklists:', err);
    }
  } else {
    results.customChecklists = true;
  }

  // Import recipes & meal plan
  if (profileData.data.recipes) {
    try {
      const recipesSection = profileData.data.recipes as any;
      const recipeIdMap = new Map<string, string>();
      let hadError = false;

      // Clear existing meal plan in the current scope so that
      // imported data replaces, rather than merges with, old items.
      try {
        const existingMealPlanRes = await fetch(getApiUrl(`meal-plan${familyQuery}`), {
          headers: {
            'Authorization': headers['Authorization'],
          },
        });
        if (existingMealPlanRes.ok) {
          const existingMealJson = await existingMealPlanRes.json();
          const existingMealData = (existingMealJson as any)?.data ?? existingMealJson;
          const existingItems: any[] = Array.isArray(existingMealData) ? existingMealData : [];
          for (const item of existingItems) {
            const delRes = await fetch(getApiUrl(`meal-plan/${item.id}${familyQuery}`), {
              method: 'DELETE',
              headers,
            });
            if (!delRes.ok) {
              hadError = true;
              console.warn('Failed to delete existing meal plan item before import', {
                id: item.id,
                status: delRes.status,
                statusText: delRes.statusText,
              });
            }
          }
        } else {
          hadError = true;
          console.warn('Failed to fetch existing meal plan before import', {
            status: existingMealPlanRes.status,
            statusText: existingMealPlanRes.statusText,
          });
        }
      } catch (clearErr) {
        hadError = true;
        console.error('Failed to clear existing meal plan before import:', clearErr);
      }

      // Clear existing recipes in the current scope so that
      // imported recipes replace, rather than merge with, old ones.
      try {
        const existingRecipesRes = await fetch(getApiUrl(`recipes${familyQuery}`), {
          headers: {
            'Authorization': headers['Authorization'],
          },
        });
        if (existingRecipesRes.ok) {
          const existingRecipesJson = await existingRecipesRes.json();
          const existingRecipesData = (existingRecipesJson as any)?.data ?? existingRecipesJson;
          const existingRecipes: any[] = existingRecipesData?.recipes || existingRecipesData || [];
          for (const recipe of existingRecipes) {
            const delRes = await fetch(getApiUrl(`recipes/${recipe.id}${familyQuery}`), {
              method: 'DELETE',
              headers,
            });
            if (!delRes.ok) {
              hadError = true;
              console.warn('Failed to delete existing recipe before import', {
                id: recipe.id,
                status: delRes.status,
                statusText: delRes.statusText,
              });
            }
          }
        } else {
          hadError = true;
          console.warn('Failed to fetch existing recipes before import', {
            status: existingRecipesRes.status,
            statusText: existingRecipesRes.statusText,
          });
        }
      } catch (clearErr) {
        hadError = true;
        console.error('Failed to clear existing recipes before import:', clearErr);
      }

      // Import recipes
      if (Array.isArray(recipesSection.recipes)) {
        for (const recipe of recipesSection.recipes) {
          const body = {
            dish: recipe.dish,
            description: recipe.description,
            servings: recipe.servings,
            ingredients: (recipe.ingredients || []).map((ing: any) => ({
              id: ing.id || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
              item: ing.item,
              quantity: ing.quantity,
              unit: ing.unit,
              order: ing.order,
            })),
            instructions: (recipe.instructions || []).map((step: any, index: number) => ({
              id: step.id || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
              step: step.step ?? index + 1,
              text: step.text,
            })),
            tags: recipe.tags || [],
            isPrivate: !!recipe.isPrivate,
          };

          const res = await fetch(getApiUrl(`recipes${familyQuery}`), {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
          });

          if (res.ok) {
            const created = await res.json();
            const env = created as any;
            const createdRecipe = env?.data ?? env;
            if (createdRecipe?.id && recipe.id) {
              recipeIdMap.set(recipe.id, createdRecipe.id);
            }
          } else {
            hadError = true;
            console.warn('Failed to import recipe', {
              status: res.status,
              statusText: res.statusText,
            });
          }
        }
      }

      // Import meal plan (if any)
      if (Array.isArray(recipesSection.mealPlan)) {
        // Preserve order by position if present
        const sortedItems = [...recipesSection.mealPlan].sort((a: any, b: any) => {
          const pa = typeof a.position === 'number' ? a.position : 0;
          const pb = typeof b.position === 'number' ? b.position : 0;
          return pa - pb;
        });

        for (const item of sortedItems) {
          const itemType = item.type || 'recipe';
          if (itemType === 'recipe') {
            const newRecipeId = recipeIdMap.get(item.recipeId) || undefined;
            if (!newRecipeId) continue;
            const createMealRes = await fetch(getApiUrl(`meal-plan${familyQuery}`), {
              method: 'POST',
              headers,
              body: JSON.stringify({
                type: 'recipe',
                recipeId: newRecipeId,
                recipeName: item.recipeName,
              }),
            });
            if (!createMealRes.ok) {
              hadError = true;
              console.warn('Failed to import meal plan recipe item', {
                status: createMealRes.status,
                statusText: createMealRes.statusText,
              });
            }
          } else if (itemType === 'marker') {
            const createMarkerRes = await fetch(getApiUrl(`meal-plan${familyQuery}`), {
              method: 'POST',
              headers,
              body: JSON.stringify({
                type: 'marker',
                markerLabel: item.markerLabel,
                markerEndDate: item.markerEndDate,
              }),
            });
            if (!createMarkerRes.ok) {
              hadError = true;
              console.warn('Failed to import meal plan marker item', {
                status: createMarkerRes.status,
                statusText: createMarkerRes.statusText,
              });
            }
          }
        }
      }

      results.recipes = !hadError;
      results.mealPlan = !hadError;
    } catch (err) {
      console.error('Failed to import recipes/meal plan:', err);
    }
  } else {
    results.recipes = true;
    results.mealPlan = true;
  }

  // Import knowledge read status
  if (profileData.data.knowledge?.readArticles && (profileData.data.knowledge.readArticles as any[]).length > 0) {
    try {
      for (const a of profileData.data.knowledge.readArticles as any[]) {
        const body = {
          articleId: a.articleId,
          areaKey: a.areaKey,
          pointsAwarded: a.pointsAwarded ?? 0,
        };
        const res = await fetch(getApiUrl('users/me/articles/read'), {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          console.warn('Failed to import read article', a);
        }
      }
      results.knowledge = true;
    } catch (err) {
      console.error('Failed to import knowledge read status:', err);
    }
  } else {
    results.knowledge = true;
  }

  // Check if any import failed
  const failed = Object.entries(results).filter(([_, success]) => !success);
  if (failed.length > 0) {
    throw new Error(`Failed to import: ${failed.map(([name]) => name).join(', ')}`);
  }
}

/**
 * Download profile export as JSON file
 */
export function downloadProfileExport(profileData: ProfileExport, filename?: string): void {
  const json = JSON.stringify(profileData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `beredskapshubben-profile-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Read profile export from uploaded JSON file
 */
export function readProfileExport(file: File): Promise<ProfileExport> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target?.result as string);
        
        // Validate basic structure
        if (!json.version || !json.exportedAt || !json.data) {
          reject(new Error('Invalid profile export format'));
          return;
        }
        
        resolve(json as ProfileExport);
      } catch (err) {
        reject(new Error('Failed to parse JSON file'));
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}
